<template>
    <div class="BulkImportCorrectionTable">
        <Alert v-if="additionalAlertConfig" :type="additionalAlertConfig.type" :title="additionalAlertConfig.title" />
        <Alert type="danger">
            <template #title>
                <FontAwesomeIcon :icon="faExclamationTriangle" />
                {{ modelValue.length }} entities could not be validated. Please fix the errors below.
            </template>
        </Alert>
        <Table>
            <template #head>
                <HeadCell v-for="property in flatProperties" :key="property.form.key">
                    <div class="BulkImportCorrectionTable__header">
                        {{ property.form.schema.title }} <span v-if="property.required">(Required)</span>
                    </div>
                    <div class="BulkImportCorrectionTable__headerHint">
                        {{ getHeaderSubText(property.form) }}
                    </div>
                </HeadCell>
            </template>
            <BulkImportCorrectionTableRow
                v-for="(item, index) in pageValue"
                :key="index"
                :form="form"
                :properties="flatProperties"
                :list-of-custom-properties-prop-names="listOfCustomPropertiesPropNames"
                :list-of-exception-prop-names="listOfExceptionPropNames"
                :value="item"
                :index="index"
                @input="valueChange($event, index)"
            />
        </Table>
        <Pagination
            v-if="modelValue.length > perPage"
            v-model="paginatedValue"
            :items="modelValue"
            :per-page="perPage"
        />
    </div>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import orderBy from 'lodash/orderBy';

    import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import { Alert } from '@evidentid/dashboard-commons/components/Alert';
    import { AlertType } from '@evidentid/dashboard-commons/components/Alert/types';
    import { Pagination } from '@evidentid/dashboard-commons/components/Pagination';
    import { HeadCell, Table } from '@evidentid/dashboard-commons/components/Table';
    import {
        JsonForm,
        JsonFormObject,
        JsonFormProperty,
        JsonFormType,
    } from '@evidentid/json-schema/interfaces/JsonForm';

    import { BaseEntityInput } from '@/modules/entity-management/types';

    import BulkImportCorrectionTableRow from '../BulkImportCorrectionTableRow/BulkImportCorrectionTableRow.vue';

    const headerHintTexts: Record<string, string> = {
        string: 'Must contain letters',
        email: 'Format as hi@email.com',
        date: 'MM/DD/YYYY',
        phone: 'Format as (201) 555-0123',
        integer: 'Must contain whole numbers',
        number: 'Must contain numbers',
        boolean: 'Must contain Yes/No',
    };

    export default defineComponent({
        name: 'BulkImportCorrectionTable',
        components: {
            Alert,
            Table,
            HeadCell,
            FontAwesomeIcon,
            BulkImportCorrectionTableRow,
            Pagination,
        },
        props: {
            form: {
                type: Object as PropType<JsonFormObject>,
                required: true,
            },
            additionalAlertConfig: {
                type: Object as PropType<{ type: AlertType, title: string } | null>,
                default: null,
            },
            modelValue: {
                type: Array as PropType<BaseEntityInput[]>,
                default: (): BaseEntityInput[] => [] as BaseEntityInput[],
            },
            csvColumnKeys: {
                type: Array as PropType<string[]>,
                required: true,
            },
        },
        emits: [ 'update:modelValue' ],
        data() {
            return {
                faExclamationTriangle,
                paginatedValue: [] as BaseEntityInput[],
                perPage: 10,
                nonStandardFieldNames: [ 'insuredFields', 'exceptions' ],
                flatProperties: [] as JsonFormProperty[],
                listOfCustomPropertiesPropNames: [] as string[],
                listOfExceptionPropNames: [] as string[],
            };
        },
        computed: {
            pageValue(): BaseEntityInput[] {
                return this.modelValue.length > this.perPage ? this.paginatedValue : this.modelValue;
            },
        },
        watch: {
            form: {
                immediate: true,
                handler() {
                    const formProperties = this.form.getProperties();
                    const customPropertiesForm = formProperties.find((prop) => prop.name === 'insuredFields')?.form;
                    const customPropertiesFormProps = customPropertiesForm?.type === JsonFormType.object
                        ? customPropertiesForm.getProperties()
                        : [];
                    this.listOfCustomPropertiesPropNames = customPropertiesFormProps.map((prop) => prop.name);
                    const exceptionForms = formProperties.find((prop) => prop.name === 'exceptions')?.form;
                    const exceptionsProperties = exceptionForms?.type === JsonFormType.object
                        ? exceptionForms.getProperties()
                        : [];
                    this.listOfExceptionPropNames = exceptionsProperties.map((prop) => prop.name);
                    const standardFieldFormProps = formProperties
                        .filter((prop) => !this.nonStandardFieldNames.includes(prop.name));
                    const properties = [
                        ...standardFieldFormProps,
                        ...customPropertiesFormProps,
                        ...exceptionsProperties,
                    ];
                    this.flatProperties = properties.filter(Boolean);
                },
            },
        },
        methods: {
            valueChange(value: BaseEntityInput, index: number): void {
                const values = [
                    ...this.modelValue.slice(0, index),
                    value,
                    ...this.modelValue.slice(index + 1),
                ];
                this.$emit('update:modelValue', values);
            },
            getHeaderSubText(form: JsonForm): string {
                const key = form.type === 'string' && form.schema.format ? form.schema.format : form.type;
                return headerHintTexts[key] || '';
            },
        },
    });
</script>
